/*
 * df-perso-analytics.js — los 5 eventos GA4 del personalizador.
 *
 * Hoy el personalizador es una caja negra: concentra la fuga del 95% que no
 * llega a añadir al carrito y no emite un solo evento. Sin esto, cualquier
 * decisión sobre el configurador es una opinión.
 *
 * Contrato (README §5, "Contrato común de los dos personalizadores"):
 *   df_perso_open      { producto, variante, origen: 'pdp' | 'pagina' }
 *   df_perso_step      { paso: 1|2|3, ms_desde_apertura }
 *   df_perso_field     { campo, accion: 'default' | 'editado' }
 *   df_perso_abandon   { ultimo_paso, ms_total }
 *   df_perso_complete  { ms_total, campos_editados }
 *
 * Sirve para los DOS personalizadores:
 *   A · InBlue / Teeinblue (embed, en producción) — se detecta por su DOM y
 *       por el evento 'teeinblue-event-component-injected'.
 *   B · El propio (en desarrollo) — habla por la API pública de abajo, que es
 *       la fuente de verdad cuando existe.
 *
 * API pública para el personalizador propio:
 *   window.dfPerso.open(ctx)          → fuerza df_perso_open
 *   window.dfPerso.step(n)            → df_perso_step
 *   window.dfPerso.field(nombre, ed)  → df_perso_field (ed = true si editado)
 *   window.dfPerso.complete()         → df_perso_complete
 *   window.dfPerso.isConfigured()     → lo consulta la barra sticky de la PDP
 *
 * O por eventos, si prefiere no depender del orden de carga:
 *   document.dispatchEvent(new CustomEvent('df:perso:step', { detail: { paso: 2 } }))
 *
 * SOBRE df_perso_step Y TEEINBLUE (comprobado en producción, 375px):
 *   Teeinblue NO tiene pasos. Es un formulario plano de 9 campos a la vista a
 *   la vez, sin wizard, sin barra de progreso y sin ninguna clase o atributo
 *   que marque un paso. Así que en los productos de Teeinblue este evento no
 *   se emite: no hay nada que contar, y contar un paso inventado sería peor
 *   que no tener el dato.
 *
 *   Los otros cuatro eventos sí salen en Teeinblue, que es donde está hoy la
 *   fuga. `df_perso_step` se llenará según el personalizador propio vaya
 *   cogiendo catálogo, llamando a window.dfPerso.step(n) en cada paso.
 */
(function () {
  'use strict';

  var CTX = window.dfPersoContext || {};
  if (CTX.enabled === false) return;

  /*
   * Aquí SÍ van los dos personalizadores, al revés que en la barra sticky:
   *   · Teeinblue (#tee-artwork-form) es el que está hoy en todo el catálogo y
   *     donde se pierde el 95%. Medirlo es justo el objetivo.
   *   · El propio (.dfp-personalizer) está en un producto y va a ir creciendo.
   * La barra sticky sólo cambia de comportamiento con el propio, porque es el
   * único que se lleva por delante el botón de añadir nativo.
   */
  var HOST_SELECTORS = CTX.hostSelector ||
    '.dfp-personalizer, [data-df-personalizer], #tee-artwork-form, .tee-campaign-form-wrapper';
  var FIELD_SELECTORS = 'input, select, textarea';
  // Sólo del personalizador propio: Teeinblue no tiene pasos (ver cabecera).
  var STEP_SELECTORS = CTX.stepSelector ||
    '[data-df-perso-step], .dfp-step.is-active, [data-dfp-step].is-active';

  var openedAt = 0;
  var opened = false;
  var completed = false;
  var lastStep = 0;
  var seenFields = {};      // campo → true (sólo se reporta la 1ª interacción)
  var editedFields = {};
  var defaults = null;      // WeakMap-lite: valores al montar, para saber si algo se editó

  // ==========================================================
  // Envío a GA4 · mismo patrón que df-smart.js y df-quiz-page
  // ==========================================================
  function track(eventName, props) {
    props = props || {};
    try {
      (window.dataLayer = window.dataLayer || []).push(
        Object.assign({ event: eventName }, props)
      );
      if (window.gtag) window.gtag('event', eventName, props);
    } catch (e) { /* la analítica nunca puede tumbar la página */ }
  }

  function now() { return Date.now(); }
  function since() { return openedAt ? now() - openedAt : 0; }

  function host() {
    try { return document.querySelector(HOST_SELECTORS); } catch (e) { return null; }
  }

  function fieldName(el) {
    // Lo que entienda un humano al leer el informe, en este orden.
    var lbl = el.getAttribute('data-df-field') ||
              el.getAttribute('aria-label') ||
              el.getAttribute('placeholder') ||
              el.name || el.id || '';
    if (!lbl && el.id) {
      var forLbl = document.querySelector('label[for="' + el.id + '"]');
      if (forLbl) lbl = forLbl.textContent;
    }
    return String(lbl).trim().slice(0, 60) || 'sin-nombre';
  }

  function fields() {
    var h = host();
    if (!h) return [];
    return Array.prototype.slice.call(h.querySelectorAll(FIELD_SELECTORS))
      .filter(function (el) { return el.type !== 'hidden'; });
  }

  function snapshotDefaults() {
    defaults = {};
    fields().forEach(function (el) {
      defaults[fieldName(el)] = valueOf(el);
    });
  }

  function valueOf(el) {
    if (el.type === 'checkbox' || el.type === 'radio') return el.checked ? '1' : '0';
    if (el.type === 'file') return el.files && el.files.length ? 'file:' + el.files.length : '';
    return el.value == null ? '' : String(el.value);
  }

  // ==========================================================
  // 1 · OPEN
  // ==========================================================
  function open(extra) {
    if (opened) return;
    opened = true;
    openedAt = now();
    snapshotDefaults();
    track('df_perso_open', {
      producto: CTX.producto || '',
      variante: CTX.variante || '',
      origen: CTX.origen || 'pdp',
      // Cuántos campos llegan ya rellenos: mide la promesa de "se puede llegar
      // al final sin tocar nada".
      campos_con_default: countPrefilled()
    });
    if (extra && typeof extra.paso === 'number') step(extra.paso);
    watchSteps();
  }

  function countPrefilled() {
    var n = 0;
    fields().forEach(function (el) { if (valueOf(el)) n++; });
    return n;
  }

  // ==========================================================
  // 2 · STEP
  // ==========================================================
  function step(n) {
    n = Number(n);
    if (!n || n === lastStep) return;
    lastStep = n;
    track('df_perso_step', { paso: n, ms_desde_apertura: since() });
  }

  function readStepFromDom() {
    var h = host();
    if (!h) return 0;
    var el;
    try { el = h.querySelector(STEP_SELECTORS) || document.querySelector(STEP_SELECTORS); }
    catch (e) { return 0; }
    if (!el) return 0;
    var raw = el.getAttribute('data-df-perso-step') ||
              el.getAttribute('data-tee-step') ||
              el.getAttribute('data-step') ||
              el.textContent;
    var m = String(raw || '').match(/\d+/);
    return m ? Number(m[0]) : 0;
  }

  function watchSteps() {
    if (typeof MutationObserver !== 'function') return;
    var h = host();
    if (!h) return;
    var queued = false;
    var mo = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        var n = readStepFromDom();
        if (n) step(n);
      });
    });
    mo.observe(h, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-step', 'data-tee-step', 'data-df-perso-step'] });
    var n0 = readStepFromDom();
    if (n0) step(n0);
  }

  // ==========================================================
  // 3 · FIELD
  // ==========================================================
  function field(nombre, editado) {
    if (seenFields[nombre]) return;
    seenFields[nombre] = true;
    if (editado) editedFields[nombre] = true;
    track('df_perso_field', {
      campo: nombre,
      accion: editado ? 'editado' : 'default',
      ms_desde_apertura: since()
    });
  }

  function onFieldInteraction(e) {
    var el = e.target;
    if (!el || !el.matches || !el.matches(FIELD_SELECTORS)) return;
    var h = host();
    if (!h || !h.contains(el)) return;
    if (!opened) open();
    var nombre = fieldName(el);
    var before = defaults ? defaults[nombre] : undefined;
    var editado = before === undefined || valueOf(el) !== before;
    // Si vuelve a tocarlo y ahora sí difiere del default, se recuerda para el
    // recuento final aunque el evento ya se emitiera.
    if (editado) editedFields[nombre] = true;
    field(nombre, editado);
  }

  // ==========================================================
  // 4 · COMPLETE
  // ==========================================================
  function complete() {
    if (completed || !opened) return;
    completed = true;
    track('df_perso_complete', {
      ms_total: since(),
      campos_editados: Object.keys(editedFields).length
    });
  }

  // ==========================================================
  // 5 · ABANDON
  // ==========================================================
  function abandon() {
    if (!opened || completed) return;
    completed = true;   // no repetir en pagehide + visibilitychange
    track('df_perso_abandon', {
      ultimo_paso: lastStep || 1,
      ms_total: since()
    });
  }

  // ==========================================================
  // Enganches
  // ==========================================================
  function boot() {
    // El embed monta asíncrono: se espera al host en lugar de asumir que está.
    if (host()) { open(); }
    else if (typeof MutationObserver === 'function') {
      var mo = new MutationObserver(function () {
        if (host()) { mo.disconnect(); open(); }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
      // Red de seguridad: si a los 15s no montó, no hay personalizador que medir.
      setTimeout(function () { mo.disconnect(); }, 15000);
    }

    document.addEventListener('change', onFieldInteraction, true);
    document.addEventListener('teeinblue-event-component-injected', function () { open(); });

    // Contrato por eventos (personalizador propio, o cualquier integración)
    document.addEventListener('df:perso:open', function (e) { open(e.detail); });
    document.addEventListener('df:perso:step', function (e) { if (e.detail) step(e.detail.paso); });
    document.addEventListener('df:perso:complete', complete);

    // Añadir al carrito desde la PDP o desde el personalizador = completado
    document.addEventListener('click', function (e) {
      if (!opened || completed) return;
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('[data-df-add], [data-df-pdp-stickycta-trigger], [data-dfp-add], [data-tee-add-to-cart], .tee-add-to-cart, form[action*="/cart/add"] [type="submit"]');
      if (!btn) return;
      // La barra sticky en estado «Personalizar» todavía no añade nada.
      var sticky = btn.closest('[data-df-pdp-stickycta]');
      if (sticky && sticky.getAttribute('data-df-perso-state') === 'pending') return;
      complete();
    }, true);

    window.addEventListener('pagehide', abandon);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') abandon();
    });
  }

  /*
   * `isConfigured` NO se define aquí a propósito: saber si un diseño está
   * listo es cosa del personalizador, no de la analítica. Si el personalizador
   * propio la publica, la barra sticky de la PDP la usará; mientras tanto, la
   * barra tira de su propia heurística de DOM.
   */
  window.dfPerso = Object.assign(window.dfPerso || {}, {
    open: open,
    step: step,
    field: field,
    complete: complete,
    abandon: abandon
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
